import { createTRPCUntypedClient, httpLink, TRPCClientError } from '@trpc/client';
import { isNetworkError } from '@charitystack/shared';
import type {
  AccountsProcedureName,
  AccountsRouteInput,
  AccountsRouteKey,
  AccountsRouteOutput,
} from '@charitystack/org-engagement-contracts';
import { accountsProcedureRoutes } from '@charitystack/org-engagement-contracts';
import { resolveAccountsRouting } from '../flags/accounts';

type WebhookRoute =
  | 'POST /webhooks/clerk'
  | 'POST /webhooks/stripe'
  | 'POST /webhooks/stripe-subscription';
type PublicRoute = 'GET /public/privacy-policy/{slug}';
export type AccountsDashboardRoute = Exclude<AccountsRouteKey, WebhookRoute | PublicRoute>;

export type TypedAccountsResponse<T> = Omit<Response, 'json'> & {
  json(): Promise<T>;
};

export type AccountsJsonResult<T> =
  | {
      ok: true;
      data: T;
      error: null;
      status: number;
      retryAfter: '';
      isNetworkError: false;
    }
  | {
      ok: false;
      data: null;
      error: string;
      status: number | null;
      retryAfter: string;
      isNetworkError: boolean;
    };

interface AccountsRequestConfig {
  context?: string;
  silent?: boolean;
  retries?: number;
  retryDelay?: number;
  backoff?: 'fixed' | 'exponential';
  retryOn5xx?: boolean;
}

export interface AccountsDashboardFacade {
  request<R extends AccountsDashboardRoute>(
    route: R,
    options: Readonly<{
      authorization: string;
      input: AccountsRouteInput<R>;
      signal?: AbortSignal;
    }>,
  ): Promise<TypedAccountsResponse<AccountsRouteOutput<R>>>;
  requestJson<R extends AccountsDashboardRoute>(
    route: R,
    options: Readonly<{
      authorization: string;
      input: AccountsRouteInput<R>;
      signal?: AbortSignal;
    }>,
    config?: AccountsRequestConfig,
  ): Promise<AccountsJsonResult<AccountsRouteOutput<R>>>;
}

const procedureByRoute = Object.fromEntries(
  (Object.entries(accountsProcedureRoutes) as [AccountsProcedureName, AccountsRouteKey][]).map(
    ([procedure, route]) => [route, procedure],
  ),
) as Record<AccountsRouteKey, AccountsProcedureName>;

function withoutTrailingSlash(value: string | undefined): string {
  return String(value).replace(/\/$/, '');
}

function legacyBaseUrl(): string {
  const configured = import.meta.env.VITE_APP_ACCOUNTS_BACKEND;
  if (configured) return withoutTrailingSlash(configured);
  return withoutTrailingSlash(import.meta.env.VITE_APP_MERCHANT_URL).replace(/\/merchant$/, '');
}

/** Preserve callers that intentionally treat missing deployment configuration as a skipped request. */
export function isAccountsRouteConfigured(route: AccountsDashboardRoute): boolean {
  if (route === 'GET /merchant') return Boolean(import.meta.env.VITE_APP_MERCHANT_URL);
  return Boolean(import.meta.env.VITE_APP_ACCOUNTS_BACKEND);
}

function legacyUrl(
  route: AccountsDashboardRoute,
  input: Readonly<Record<string, unknown>>,
): string {
  const [method, path] = route.split(' ', 2) as [string, string];
  const base = route === 'GET /merchant' ? import.meta.env.VITE_APP_MERCHANT_URL : legacyBaseUrl();
  if (method !== 'GET' && method !== 'DELETE') return `${withoutTrailingSlash(base)}${path}`;
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(input)) {
    if (value !== undefined) query.set(key, String(value));
  }
  const suffix = query.size > 0 ? `?${query}` : '';
  return `${route === 'GET /merchant' ? withoutTrailingSlash(base) : `${withoutTrailingSlash(base)}${path}`}${suffix}`;
}

function legacyRequest<R extends AccountsDashboardRoute>(
  route: R,
  {
    authorization,
    input,
    signal,
  }: {
    authorization: string;
    input: AccountsRouteInput<R>;
    signal?: AbortSignal;
  },
  request: typeof globalThis.fetch = globalThis.fetch,
): Promise<TypedAccountsResponse<AccountsRouteOutput<R>>> {
  const method = route.slice(0, route.indexOf(' '));
  const values = input as Readonly<Record<string, unknown>>;
  const { idempotencyKey, ...body } = values;
  return request(legacyUrl(route, values), {
    method,
    signal,
    headers: {
      accept: '*/*',
      'content-type': 'application/json',
      Authorization: authorization,
      ...(typeof idempotencyKey === 'string' ? { 'Idempotency-Key': idempotencyKey } : {}),
    },
    ...(method === 'GET' || method === 'DELETE' ? {} : { body: JSON.stringify(body) }),
  }) as Promise<TypedAccountsResponse<AccountsRouteOutput<R>>>;
}

interface AccountsClientOptions {
  registry?: unknown;
  candidateEndpoint?: unknown;
  request?: typeof globalThis.fetch;
}

type SelectedAccountsTransport = Readonly<{
  organizationId: string | null;
  client: AccountsDashboardFacade;
}>;

function selectAccountsTransport(
  authorization: string,
  route: AccountsDashboardRoute,
  options: AccountsClientOptions = {},
): SelectedAccountsTransport {
  const { organizationId, candidateEndpoint } = resolveAccountsRouting({
    authorization,
    route,
    ...(options.registry === undefined ? {} : { registry: options.registry }),
    ...(options.candidateEndpoint === undefined
      ? {}
      : { candidateEndpoint: options.candidateEndpoint }),
  });
  if (candidateEndpoint) {
    return {
      organizationId,
      client: createAccountsTrpcTransport({
        endpoint: candidateEndpoint,
        ...(options.request ? { request: options.request } : {}),
      }),
    };
  }
  return {
    organizationId,
    client: withJsonRequests((route, requestOptions) =>
      legacyRequest(route, requestOptions, options.request ?? globalThis.fetch),
    ),
  };
}

/**
 * Per-request Accounts router. Selection uses only the current authenticated Clerk org claim and
 * is retained for every retry in that logical request; a later request reevaluates the registry.
 */
export function createAccountsClient(options: AccountsClientOptions = {}): AccountsDashboardFacade {
  return Object.freeze({
    request<R extends AccountsDashboardRoute>(
      route: R,
      requestOptions: Readonly<{
        authorization: string;
        input: AccountsRouteInput<R>;
        signal?: AbortSignal;
      }>,
    ) {
      return selectAccountsTransport(requestOptions.authorization, route, options).client.request(
        route,
        requestOptions,
      );
    },
    requestJson<R extends AccountsDashboardRoute>(
      route: R,
      requestOptions: Readonly<{
        authorization: string;
        input: AccountsRouteInput<R>;
        signal?: AbortSignal;
      }>,
      config?: AccountsRequestConfig,
    ) {
      return selectAccountsTransport(
        requestOptions.authorization,
        route,
        options,
      ).client.requestJson(route, requestOptions, config);
    },
  });
}

function withJsonRequests(request: AccountsDashboardFacade['request']): AccountsDashboardFacade {
  return {
    request,
    async requestJson<R extends AccountsDashboardRoute>(
      route: R,
      options: Readonly<{
        authorization: string;
        input: AccountsRouteInput<R>;
        signal?: AbortSignal;
      }>,
      config: AccountsRequestConfig = {},
    ): Promise<AccountsJsonResult<AccountsRouteOutput<R>>> {
      const {
        context = 'Accounts API call',
        silent = false,
        retries = 1,
        retryDelay = 1000,
        backoff = 'fixed',
        retryOn5xx = false,
      } = config;
      let lastError: unknown = null;

      for (let attempt = 0; attempt <= retries; attempt += 1) {
        try {
          const response = await request(route, options);
          if (!response.ok) {
            if (retryOn5xx && response.status >= 500 && attempt < retries) {
              const delay = backoff === 'exponential' ? retryDelay * 2 ** attempt : retryDelay;
              if (!silent) {
                console.warn(
                  `[accountsClient] ${context} - Server error ${response.status}, retrying in ${delay}ms (attempt ${attempt + 1}/${retries})...`,
                );
              }
              await new Promise((resolve) => setTimeout(resolve, delay));
              continue;
            }
            const errorText = await response.text().catch(() => 'Unknown error');
            return {
              ok: false,
              data: null,
              error: `HTTP ${response.status}: ${errorText}`,
              status: response.status,
              retryAfter: response.headers.get('Retry-After') || '',
              isNetworkError: false,
            };
          }
          return {
            ok: true,
            data: await response.json(),
            error: null,
            status: response.status,
            retryAfter: '',
            isNetworkError: false,
          };
        } catch (error) {
          lastError = error;
          const networkError = isAccountsNetworkError(error);
          if (networkError && attempt < retries) {
            const delay = backoff === 'exponential' ? retryDelay * 2 ** attempt : retryDelay;
            if (!silent) {
              console.warn(
                `[accountsClient] ${context} - Network error, retrying in ${delay}ms (attempt ${attempt + 1}/${retries})...`,
              );
            }
            await new Promise((resolve) => setTimeout(resolve, delay));
            continue;
          }
          if (!silent) console.error(`[accountsClient] ${context} failed:`, error);
          if (!networkError) break;
        }
      }

      return {
        ok: false,
        data: null,
        error: lastError instanceof Error ? lastError.message : 'Unknown error',
        status: null,
        retryAfter: '',
        isNetworkError: isAccountsNetworkError(lastError),
      };
    },
  };
}

function isAccountsNetworkError(error: unknown): boolean {
  return isNetworkError(error) || (error instanceof TRPCClientError && isNetworkError(error.cause));
}

/** Sole active dashboard binding. The empty registry keeps every organization on legacy. */
export const accountsClient: AccountsDashboardFacade = createAccountsClient();

export async function requestAccountsJsonWithAuthRetry<R extends AccountsDashboardRoute>(
  route: R,
  input: AccountsRouteInput<R>,
  {
    getAuthorization,
    invalidateAuthorization,
    context = 'Accounts API call',
    maxRetries = 3,
    retryDelay = 1000,
  }: {
    getAuthorization: () => Promise<string>;
    invalidateAuthorization?: () => Promise<unknown>;
    context?: string;
    maxRetries?: number;
    retryDelay?: number;
  },
): Promise<AccountsJsonResult<AccountsRouteOutput<R>>> {
  let lastResult: AccountsJsonResult<AccountsRouteOutput<R>> | null = null;
  let authorization = await getAuthorization();
  const selected = selectAccountsTransport(authorization, route);
  for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
    if (attempt > 0) {
      authorization = await getAuthorization();
      const refreshedOrganizationId = resolveAccountsRouting({
        authorization,
        route,
      }).organizationId;
      if (refreshedOrganizationId !== selected.organizationId) return lastResult!;
    }
    lastResult = await selected.client.requestJson(
      route,
      { authorization, input },
      { context, retries: 0 },
    );
    if (lastResult.ok || lastResult.status !== 403 || attempt === maxRetries) {
      return lastResult;
    }
    try {
      await invalidateAuthorization?.();
    } catch {
      // Cache invalidation failure should not break the existing retry path.
    }
    await new Promise((resolve) => setTimeout(resolve, retryDelay));
  }
  return lastResult!;
}

/** Explicit candidate transport for protocol tests and later INF-77 selection. */
export function createAccountsTrpcTransport({
  endpoint,
  request: fetcher = globalThis.fetch,
}: {
  endpoint: string;
  request?: typeof globalThis.fetch;
}): AccountsDashboardFacade {
  if (!endpoint) throw new Error('An Accounts tRPC endpoint is required');
  return withJsonRequests(async function request<R extends AccountsDashboardRoute>(
    route: R,
    options: Readonly<{
      authorization: string;
      input: AccountsRouteInput<R>;
      signal?: AbortSignal;
    }>,
  ): Promise<TypedAccountsResponse<AccountsRouteOutput<R>>> {
    const client = createTRPCUntypedClient({
      links: [
        httpLink({
          url: withoutTrailingSlash(endpoint),
          fetch: fetcher,
          headers: { 'x-cs-authorization': options.authorization },
        }),
      ],
    });
    const procedure = `org.accounts.${procedureByRoute[route]}`;
    try {
      const body = route.startsWith('GET ')
        ? await client.query(procedure, options.input, { signal: options.signal })
        : await client.mutation(procedure, options.input, { signal: options.signal });
      return new Response(JSON.stringify(body), {
        status: 200,
        headers: { 'content-type': 'application/json' },
      }) as TypedAccountsResponse<AccountsRouteOutput<R>>;
    } catch (error) {
      if (!(error instanceof TRPCClientError)) throw error;
      const status = error.data?.httpStatus;
      if (typeof status !== 'number' || status < 400 || status > 599) throw error;
      const headers = new Headers({ 'content-type': 'application/json' });
      const retryAfter = (error.meta as { response?: Response } | undefined)?.response?.headers.get(
        'retry-after',
      );
      if (retryAfter) headers.set('retry-after', retryAfter);
      return new Response(
        JSON.stringify({
          error: { message: error.message, code: error.data?.code, data: error.data },
        }),
        { status, headers },
      ) as TypedAccountsResponse<AccountsRouteOutput<R>>;
    }
  });
}
